import { Box, Modal, Text, Loader, Center, Flex } from '@mantine/core';
import { IconBrandSlack } from '@tabler/icons';
import { useState, useRef, useMemo } from 'react';
import moment from 'moment';
import { useSlackConversation, useFlattenedSlackDays } from './useSlackConversation';
import { ConversationView } from './ConversationView';
import { ExcerptNavigation } from './ExcerptNavigation';
import { buildExcerptMapping, type EvidenceExcerpt, type CxItemIdRef } from './excerptMapping';

function parseChannelIdAndDate(cxOriginId: string): {
  channelId: string;
  centerDate: string;
  cutMode: 'day' | 'thread';
} | null {
  if (!cxOriginId || typeof cxOriginId !== 'string') return null;
  if (cxOriginId.includes('_t_')) {
    const [channelId, threadTs] = cxOriginId.split('_t_');
    if (!channelId || !threadTs) return null;
    const tsNum = parseFloat(threadTs);
    if (Number.isNaN(tsNum)) return null;
    const centerDate = moment.unix(tsNum).format('YYYY-MM-DD');
    return { channelId, centerDate, cutMode: 'thread' };
  }
  const lastUnderscore = cxOriginId.lastIndexOf('_');
  if (lastUnderscore < 0) return null;
  const channelId = cxOriginId.slice(0, lastUnderscore);
  const centerDate = cxOriginId.slice(lastUnderscore + 1);
  if (!channelId || !centerDate) return null;
  return { channelId, centerDate, cutMode: 'day' };
}

/** Normalize cxItemId (string or { $oid } from API) to a comparable string */
function cxItemIdToString(id: CxItemIdRef | null | undefined): string | null {
  if (id == null) return null;
  if (typeof id === 'string') return id;
  if (id && typeof id === 'object' && typeof (id as { $oid?: string }).$oid === 'string') {
    return (id as { $oid: string }).$oid;
  }
  return null;
}

export interface SlackConversationModalProps {
  item: {
    cxItemId?: CxItemIdRef;
    cxOriginId?: string;
    cxItem?: { originId?: string; channelId?: string; channelName?: string; originalItem?: { activityDate?: string } };
    excerpts?: EvidenceExcerpt[];
  };
  opened: boolean;
  onClose: () => void;
}

export function SlackConversationModal({ item, opened, onClose }: SlackConversationModalProps) {
  const cxOriginId = item?.cxOriginId ?? item?.cxItem?.originId ?? null;
  const parsed = useMemo(() => parseChannelIdAndDate(cxOriginId || ''), [cxOriginId]);
  const channelId = parsed?.channelId ?? item?.cxItem?.channelId ?? '';
  const centerDate = parsed?.centerDate ?? item?.cxItem?.originalItem?.activityDate ?? '';
  const cutMode = parsed?.cutMode ?? 'day';
  const centerDateKey =
    cutMode === 'thread' && cxOriginId && parsed
      ? `${parsed.centerDate}_t_${cxOriginId.split('_t_')[1] ?? ''}`
      : centerDate;

  const excerptRefsArray = useRef<(HTMLDivElement | null)[]>([]);
  const [currentExcerptIndex, setCurrentExcerptIndex] = useState(0);

  const slackQuery = useSlackConversation({
    channelId,
    centerDate,
    cutMode,
    enabled: opened && !!channelId && !!centerDate,
  });

  const flattenedDays = useFlattenedSlackDays(slackQuery.data);
  const allExcerpts = item?.excerpts ?? [];
  const currentCxItemIdStr = useMemo(() => cxItemIdToString(item?.cxItemId), [item?.cxItemId]);
  const excerpts = useMemo(() => {
    if (currentCxItemIdStr == null) return allExcerpts;
    return allExcerpts.filter((ex) => {
      const exCxId = cxItemIdToString(ex.cxItemId);
      return exCxId == null || exCxId === currentCxItemIdStr;
    });
  }, [allExcerpts, currentCxItemIdStr]);
  const excerptCount = excerpts.length;

  const excerptMapping = useMemo(() => {
    if (excerptCount === 0 || flattenedDays.length === 0) return undefined;
    if (excerptRefsArray.current.length < excerptCount) {
      excerptRefsArray.current = Array(excerptCount)
        .fill(null)
        .map((_, i) => excerptRefsArray.current[i] ?? null);
    }
    return buildExcerptMapping(excerpts, flattenedDays, excerptRefsArray);
  }, [excerpts, flattenedDays, excerptCount]);

  const scrollToExcerpt = (index: number) => {
    const i = Math.max(0, Math.min(index, excerptCount - 1));
    setCurrentExcerptIndex(i);
    const tryScroll = () => {
      const el = excerptRefsArray.current[i];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    requestAnimationFrame(() => {
      tryScroll();
      if (!excerptRefsArray.current[i]) {
        setTimeout(tryScroll, 150);
      }
    });
  };

  const channelName = flattenedDays[0]?.channelName ?? item?.cxItem?.channelName ?? 'Slack';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={900}
      padding={0}
      zIndex={999999}
      title={
        <Flex align="center" gap="sm">
          <IconBrandSlack size={22} stroke={1.5} />
          <Text fw={600} size="md">
            #{channelName}
          </Text>
        </Flex>
      }
      styles={{
        title: { marginRight: 40, marginLeft: 16 },
        close: { margin: "10px" },
      }}
    >
      <Box sx={{ borderTop: '1px solid #D8D8DB' }} h="80vh" style={{ display: 'flex', flexDirection: 'column' }}>
        <ExcerptNavigation
          excerptCount={excerptCount}
          currentExcerptIndex={currentExcerptIndex}
          onJumpToFirst={() => scrollToExcerpt(0)}
          onPrevious={() => scrollToExcerpt(currentExcerptIndex - 1)}
          onNext={() => scrollToExcerpt(currentExcerptIndex + 1)}
        />
        {slackQuery.isLoading ? (
          <Center style={{ flex: 1 }}>
            <Loader size="md" />
          </Center>
        ) : slackQuery.isError ? (
          <Center style={{ flex: 1 }}>
            <Text c="dimmed">Failed to load conversation.</Text>
          </Center>
        ) : (
          <ConversationView
            days={flattenedDays}
            centerDate={centerDateKey}
            isFetchingNextPage={slackQuery.isFetchingNextPage}
            isFetchingPreviousPage={slackQuery.isFetchingPreviousPage}
            hasNextPage={slackQuery.hasNextPage ?? false}
            hasPreviousPage={slackQuery.hasPreviousPage ?? false}
            fetchNextPage={() => slackQuery.fetchNextPage()}
            fetchPreviousPage={() => slackQuery.fetchPreviousPage()}
            excerptMapping={excerptMapping}
          />
        )}
      </Box>
    </Modal>
  );
}

export default SlackConversationModal;
